/*
 * MY — Hero slideshow
 *
 * Crossfades the hero's background slides. The copy, veil and buttons are
 * fixed, so only the photo layer changes.
 *
 * Deliberate behaviours:
 *  - Slide 1 is marked active in Liquid. This script never has to run for the
 *    hero to show a photo.
 *  - Autoplay stops on hover, on keyboard focus inside the hero, when the tab
 *    is hidden, and when the hero scrolls out of view.
 *  - prefers-reduced-motion turns autoplay off entirely; the dots still work.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function Hero(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('.my-hero__slide'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-my-hero-dot]'));
    if (this.slides.length < 2) return;

    this.index = Math.max(0, this.slides.indexOf(root.querySelector('.my-hero__slide.is-active')));
    this.interval = parseInt(root.dataset.interval, 10) || 6000;
    this.wantsAutoplay = root.dataset.autoplay !== 'false';
    this.timer = null;
    this.paused = { focus: false, hidden: false, offscreen: false };

    this.bind();
    this.show(this.index);
    this.resume();
  }

  Hero.prototype.bind = function () {
    var self = this;

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.show(parseInt(dot.dataset.myHeroDot, 10));
        // A deliberate click should get its full dwell time, not the remainder
        // of the interval that was already running.
        self.restart();
      });
    });

    // Deliberately NO pause-on-hover. The hero is a full-width band with no
    // content to read, so a resting cursor anywhere over it would stall the
    // slideshow — which just reads as "it isn't rotating". Keyboard focus
    // still pauses, because that means someone is tabbing the dots.
    this.root.addEventListener('focusin', function () { self.setPause('focus', true); });
    this.root.addEventListener('focusout', function () { self.setPause('focus', false); });

    this.root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') { event.preventDefault(); self.show(self.index + 1); self.restart(); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); self.show(self.index - 1); self.restart(); }
    });

    this.onVisibility = function () { self.setPause('hidden', document.hidden); };
    document.addEventListener('visibilitychange', this.onVisibility);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(function (entries) {
        self.setPause('offscreen', !entries[0].isIntersecting);
      }, { threshold: 0.15 });
      this.observer.observe(this.root);
    }

    // Reduced motion can be toggled while the page is open.
    this.onMotion = function () { self.resume(); };
    if (REDUCED.addEventListener) REDUCED.addEventListener('change', this.onMotion);
  };

  Hero.prototype.setPause = function (reason, value) {
    this.paused[reason] = value;
    this.resume();
  };

  Hero.prototype.show = function (next) {
    var count = this.slides.length;
    this.index = ((next % count) + count) % count;

    this.slides.forEach(function (slide, i) {
      var active = i === this.index;
      slide.classList.toggle('is-active', active);
      // aria-hidden must not hide the slide the visitor is looking at, and the
      // inactive ones are decorative photographs behind fixed copy.
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    }, this);

    this.dots.forEach(function (dot, i) {
      var active = i === this.index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }, this);
  };

  Hero.prototype.stop = function () {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  };

  Hero.prototype.resume = function () {
    this.stop();
    if (!this.wantsAutoplay || REDUCED.matches) return;
    var blocked = Object.keys(this.paused).some(function (k) { return this.paused[k]; }, this);
    if (blocked) return;

    var self = this;
    this.timer = setInterval(function () { self.show(self.index + 1); }, this.interval);
  };

  Hero.prototype.restart = function () {
    this.resume();
  };

  Hero.prototype.destroy = function () {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.observer) this.observer.disconnect();
    if (REDUCED.removeEventListener) REDUCED.removeEventListener('change', this.onMotion);
  };

  var instances = new WeakMap();

  function init(scope) {
    (scope || document).querySelectorAll('[data-my-hero-slideshow]').forEach(function (root) {
      if (instances.has(root)) return;
      instances.set(root, new Hero(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: sections are re-rendered in place, so re-init the new markup
  // and tear the old timer down.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-my-hero-slideshow]').forEach(function (root) {
      var hero = instances.get(root);
      if (hero && hero.destroy) hero.destroy();
      instances.delete(root);
    });
  });
})();
